'use client';

import { useState } from 'react';
import { useAppStore } from '@/stores/app-store';
import { DashWidget, Plant, Device } from '@/lib/types';
import { NO_VALUE } from '@/lib/device-status';

interface Props { widget: DashWidget; plant: Plant; }

/*
 * HVAC / VRF-VRV SCADA — an ISOMETRIC (3D-style) mimic of an office building.
 * Concept: Momospace Cloud ◄ Mspace V6 ► TIS box ─control─► VRF
 *   Rooftop Outdoor Unit → refrigerant pipes → ceiling cassette Indoor Units
 *   (one per zone) blowing cool air into a cutaway top floor.
 *
 * Reads one showcase device (config_data.staticDevices) by widget.deviceSN, so
 * the demo runs on fixed values with no telemetry tables. Zones auto-discover
 * from zoneN_temp keys. Controls (tap a unit / setpoint / mode) are demo only —
 * local state + a "sent to TIS box" toast, no hardware write.
 */
export default function HvacScadaWidget({ widget, plant }: Props) {
  const th = useAppStore((s) => s.lang) === 'th';
  const toast = useAppStore((s) => s.toast);

  const dev: Device | undefined =
    plant.devices.find((d) => d.sn === widget.deviceSN) || plant.devices[0];
  const d = (dev?.data || {}) as Record<string, number | string | undefined>;
  const names = dev?.fieldNames || {};
  const units = dev?.units || {};
  const online = dev ? dev.st === 'online' : false;

  const num = (k: string): number | null =>
    typeof d[k] === 'number' && !isNaN(d[k] as number) ? (d[k] as number) : null;
  const fmt = (v: number | null, dp = 1) => (v == null ? NO_VALUE : (Number.isInteger(v) ? String(v) : v.toFixed(dp)));
  const label = (k: string) => FIELD_LABELS[k]?.[th ? 'th' : 'en'] ?? names[k] ?? k;
  const unit = (k: string) => units[k] || '';
  const sysName = th ? (dev?.name || 'ระบบปรับอากาศ VRF/VRV — TIS-VRF-AC · Mspace V6') : 'VRF/VRV HVAC — TIS-VRF-AC · Mspace V6';

  const zones = [1, 2, 3, 4]
    .filter((n) => d[`zone${n}_temp`] !== undefined)
    .map((n) => ({ n, tempKey: `zone${n}_temp`, onKey: `zone${n}_on`, name: FIELD_LABELS[`zone${n}_temp`]?.[th ? 'th' : 'en'] ?? names[`zone${n}_temp`] ?? `Zone ${n}` }));
  const Z = Math.max(zones.length, 1);

  // ── demo control state ──
  const [zoneOn, setZoneOn] = useState<boolean[]>(zones.map((z) => num(z.onKey) !== 0));
  const [setpoint, setSetpoint] = useState<number>(num('setpoint') ?? 24);
  const [mode, setMode] = useState<string>(typeof d.mode === 'string' ? (d.mode as string) : 'eco');
  const [aiOn, setAiOn] = useState(false);
  const [redPct, setRedPct] = useState<number>(() => { const sv = num('save_pct'); return sv != null ? Math.round(sv) : 15; });

  const sent = (m: string) => toast((th ? 'ส่งคำสั่งไปยังกล่อง TIS-VRF: ' : 'Sent to TIS-VRF box: ') + m);
  const toggleZone = (i: number) => { setZoneOn((s) => { const nn = [...s]; nn[i] = !nn[i]; return nn; }); sent(`${zones[i].name} → ${zoneOn[i] ? 'OFF' : 'ON'}`); };
  const bump = (dv: number) => setSetpoint((s) => { const v = Math.min(30, Math.max(16, +(s + dv).toFixed(1))); sent(`Setpoint ${v}°C`); return v; });
  const pickMode = (m: string) => { setMode(m); sent(`Mode ${MODE_LABEL[m] || m}`); };
  const allZones = (on: boolean) => { setZoneOn(zones.map(() => on)); sent(th ? (on ? 'เปิดทุกโซน' : 'ปิดทุกโซน') : (on ? 'All ON' : 'All OFF')); };

  // ── palette ──
  const C = {
    cyan: 'var(--cyan)', amber: 'var(--amber)', green: 'var(--green)', red: 'var(--red)',
    blue: 'var(--blue)', gold: 'var(--gold)',
    mut: 'color-mix(in srgb, var(--text-muted) 55%, var(--text-primary))', txt: 'var(--text-primary)',
  };
  const BD = 'color-mix(in srgb, var(--text-muted) 45%, transparent)';
  const PANEL = 'var(--bg-deep)';
  // building face fills (theme-aware, subtle)
  const F_TOP = 'color-mix(in srgb, var(--bg-surface) 78%, var(--cyan))';
  const F_L = 'color-mix(in srgb, var(--bg-surface) 88%, #000)';
  const F_R = 'color-mix(in srgb, var(--bg-surface) 94%, var(--cyan))';
  const tnum = { fontVariantNumeric: 'tabular-nums' as const };

  const oat = num('oat');
  const weather = typeof d.weather === 'string' ? (d.weather as string) : '';
  const oduOn = zoneOn.some(Boolean);
  const running = zoneOn.filter(Boolean).length;

  // ── AI optimization (demo) ──
  const baseKwh = num('energy_budget') ?? 50;
  const aiTargetKwh = +(baseKwh * (1 - redPct / 100)).toFixed(1);
  const effectiveProj = aiOn ? aiTargetKwh : (num('energy_proj') ?? baseKwh);
  const toggleAi = () => setAiOn((o) => { const nx = !o; toast(nx ? (th ? `🤖 AI Optimization เปิด · เป้าลดไฟ ${redPct}% วันนี้` : `🤖 AI Optimization ON · target −${redPct}% today`) : (th ? 'AI Optimization ปิด' : 'AI Optimization OFF')); return nx; });
  const bumpRed = (dv: number) => setRedPct((p) => { const v = Math.min(40, Math.max(0, p + dv)); if (aiOn) toast(th ? `🤖 ปรับเป้าลดไฟเป็น ${v}%` : `🤖 target −${v}%`); return v; });
  const aiActions = th
    ? ['ปรับ setpoint +1°C โซนคนน้อย/ช่วงพักเที่ยง', 'หรี่/สลับคอมเพรสเซอร์ VRF ลดพีคโหลด', 'Pre-cool ก่อนช่วงค่าไฟแพง แล้วปล่อย coast', 'ปิดโซนที่ไม่ได้ใช้งานอัตโนมัติ']
    : ['Raise setpoint +1°C in low-use zones', 'Stage/dim VRF compressor to cut peak', 'Pre-cool before peak, then coast', 'Auto-off idle zones'];

  const kpis: { label: string; value: string; unit: string; color: string; hero?: boolean }[] = [
    { label: th ? 'กำลังไฟรวม' : 'Total Power', value: fmt(num('kw'), 0), unit: 'kW', color: C.amber, hero: true },
    { label: 'COP', value: fmt(num('cop'), 1), unit: '', color: C.green, hero: true },
    { label: th ? 'โหลด' : 'Load', value: fmt(num('load'), 0), unit: '%', color: C.cyan },
  ];

  // ── isometric projection ──
  const U = 26, OX = 430, OY = 140;
  const P = (x: number, y: number, z: number): [number, number] => [OX + (x - y) * 0.866 * U, OY + (x + y) * 0.5 * U - z * U];
  const s = (p: [number, number]) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`;
  const poly = (...pts: [number, number][]) => pts.map(s).join(' ');

  const FW = 11, FD = 8, WH = 4.5; // floor width/depth, wall height (single cutaway floor)
  // top-floor corners (z=0 floor plane)
  const A = (z = 0) => P(0, 0, z), B = (z = 0) => P(FW, 0, z), Cc = (z = 0) => P(FW, FD, z), D = (z = 0) => P(0, FD, z);
  // zone centres on the floor + ceiling cassette positions
  const zoneCenter = (i: number): [number, number, number] => [(i + 0.5) * (FW / Z), FD / 2, 0];
  const cassette = (i: number): [number, number, number] => [(i + 0.5) * (FW / Z), FD / 2, WH - 0.6];
  const oduPos: [number, number, number] = [1.6, -1.1, WH + 0.4];

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', gap: 10, fontFamily: 'system-ui, sans-serif', overflow: 'auto', containerType: 'inline-size' }}>
      <style>{`
        .hv-flow { stroke-dasharray: 6 8; animation: hvMove 1s linear infinite; }
        @keyframes hvMove { to { stroke-dashoffset: -14; } }
        .hv-ctrl { stroke-dasharray: 3 6; animation: hvMove 1.6s linear infinite; }
        .hv-fan { transform-box: fill-box; transform-origin: center; animation: hvSpin 2.6s linear infinite; }
        @keyframes hvSpin { to { transform: rotate(360deg); } }
        .hv-pulse { animation: hvPulse 1.5s ease-in-out infinite; }
        @keyframes hvPulse { 0%,100%{opacity:1} 50%{opacity:.4} }
        .hv-air { animation: hvAir 2.4s ease-in-out infinite; }
        @keyframes hvAir { 0%,100%{opacity:.5} 50%{opacity:.85} }
        @media (prefers-reduced-motion: reduce){ .hv-flow,.hv-ctrl,.hv-fan,.hv-pulse,.hv-air{animation:none} }

        /* responsive: adapts to the widget's own width (zoom / resize) */
        .hv-main { display: flex; gap: 12px; align-items: stretch; }
        .hv-left { flex: 1.5 1 0; min-width: 0; align-self: center; }
        .hv-side { flex: 1 1 0; min-width: 0; }
        .hv-svg { display: block; width: 100%; height: auto; }
        @container (max-width: 760px) {
          .hv-main { flex-direction: column; }
          .hv-side { flex: 1 1 auto; }
        }

        /* premium surfaces */
        .hv-card { background: linear-gradient(158deg, color-mix(in srgb, var(--bg-surface) 90%, var(--cyan)) 0%, var(--bg-surface) 52%); border: 1px solid color-mix(in srgb, var(--text-muted) 24%, transparent); border-radius: 16px; box-shadow: 0 12px 38px -18px rgba(0,0,0,.5), inset 0 1px 0 rgba(255,255,255,.06); }
        .hv-sub { background: linear-gradient(158deg, color-mix(in srgb, var(--bg-elevated) 88%, var(--cyan)), var(--bg-elevated)); border: 1px solid color-mix(in srgb, var(--text-muted) 20%, transparent); border-radius: 13px; box-shadow: inset 0 1px 0 rgba(255,255,255,.05); }
        .hv-kpi { border-radius: 13px; box-shadow: inset 0 1px 0 rgba(255,255,255,.06); transition: transform .16s ease, box-shadow .16s ease; }
        .hv-kpi:hover { transform: translateY(-2px); }
        .hv-title { font-size: 10.5px; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; color: var(--text-secondary); }
        .hv-glowon { box-shadow: 0 0 0 1px color-mix(in srgb, var(--green) 45%, transparent), 0 8px 26px -12px color-mix(in srgb, var(--green) 55%, transparent); }
      `}</style>

      {/* ── building (left) + combined info card (right) ── */}
      <div className="hv-main">
      <div className="hv-left">
      {/* header (inside left column so the info card can rise to the top) */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span className={online ? 'hv-pulse' : ''} style={{ width: 8, height: 8, borderRadius: 99, background: online ? C.green : C.red, boxShadow: online ? `0 0 6px ${C.green}` : 'none' }} />
          <span style={{ fontSize: 12.5, fontWeight: 700, color: C.txt }}>{sysName}</span>
          <span style={{ fontSize: 11, color: C.mut }}>· {running}/{zones.length} {th ? 'โซนทำงาน' : 'zones on'}</span>
          <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: 'var(--amber-dim)', color: C.amber, marginLeft: 'auto' }}>{MODE_LABEL[mode] || mode}</span>
        </div>
        <div style={{ fontSize: 11, color: C.mut, marginTop: 3 }}>
          {th ? 'อาคารสำนักงาน · Mspace V6 → กล่อง TIS → ควบคุมแอร์ VRF/VRV' : 'Office building · Mspace V6 → TIS box → VRF/VRV control'}
        </div>
      </div>
      {/* ── isometric 3D scene ── */}
      <svg className="hv-svg" viewBox="0 0 760 430" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="hv-airgrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.cyan} stopOpacity={0.55} />
            <stop offset="100%" stopColor={C.cyan} stopOpacity={0.02} />
          </linearGradient>
          <radialGradient id="hv-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={C.green} stopOpacity={0.45} />
            <stop offset="100%" stopColor={C.green} stopOpacity={0} />
          </radialGradient>
          <linearGradient id="hv-floor" x1="0" y1="0" x2="0.4" y2="1">
            <stop offset="0%" stopColor={C.cyan} stopOpacity={0.16} />
            <stop offset="100%" stopColor={C.cyan} stopOpacity={0.02} />
          </linearGradient>
          <filter id="hv-blur" x="-60%" y="-60%" width="220%" height="220%"><feGaussianBlur stdDeviation="10" /></filter>
        </defs>

        {/* soft ground shadow for depth */}
        <ellipse cx={P(FW / 2, FD / 2, 0)[0]} cy={P(FW / 2, FD / 2, 0)[1] + 34} rx={250} ry={64} fill="rgba(0,0,0,0.28)" filter="url(#hv-blur)" />

        {/* office floor slab */}
        <polygon points={poly(A(), B(), Cc(), D())} fill={F_TOP} stroke={BD} strokeWidth={1} />
        <polygon points={poly(A(), B(), Cc(), D())} fill="url(#hv-floor)" />
        {/* zone tiles on the floor */}
        {zones.map((z, i) => {
          const x0 = i * (FW / Z), x1 = (i + 1) * (FW / Z);
          const on = zoneOn[i];
          const t = num(z.tempKey);
          const over = t != null && t > setpoint + 1;
          const fill = on ? (over ? 'color-mix(in srgb, var(--red) 22%, transparent)' : 'color-mix(in srgb, var(--cyan) 20%, transparent)') : 'transparent';
          return <polygon key={i} points={poly(P(x0, 0.2, 0), P(x1 - 0.15, 0.2, 0), P(x1 - 0.15, FD - 0.2, 0), P(x0, FD - 0.2, 0))} fill={fill} stroke={C.cyan} strokeOpacity={0.25} strokeWidth={0.8} />;
        })}

        {/* two back walls with window bands (cutaway — front open) */}
        <polygon points={poly(A(0), B(0), B(WH), A(WH))} fill={F_R} fillOpacity={0.85} stroke={BD} strokeWidth={1} />
        <polygon points={poly(A(0), D(0), D(WH), A(WH))} fill={F_L} fillOpacity={0.9} stroke={BD} strokeWidth={1} />
        {[1.6, 3.6, 5.2].map((z, i) => (
          <g key={i}>
            <line x1={P(1, 0, z)[0]} y1={P(1, 0, z)[1]} x2={P(FW - 1, 0, z)[0]} y2={P(FW - 1, 0, z)[1]} stroke={C.cyan} strokeOpacity={0.18} strokeWidth={3} />
            <line x1={P(0, 1, z)[0]} y1={P(0, 1, z)[1]} x2={P(0, FD - 1, z)[0]} y2={P(0, FD - 1, z)[1]} stroke={C.cyan} strokeOpacity={0.14} strokeWidth={3} />
          </g>
        ))}

        {/* refrigerant pipes: ODU → ceiling ridge → each cassette (animated) */}
        {(() => {
          const ridgeZ = WH - 0.4;
          const ridgeStart = P(oduPos[0], 0.3, ridgeZ);
          return zones.map((_, i) => {
            const cpos = cassette(i);
            const via = P(cpos[0], 0.3, ridgeZ);
            const cc = P(cpos[0], cpos[1], cpos[2]);
            const pts = `${s(ridgeStart)} ${s(via)} ${s(cc)}`;
            return (
              <g key={`pipe${i}`}>
                <polyline points={pts} fill="none" stroke={C.cyan} strokeWidth={5} strokeOpacity={0.22} strokeLinecap="round" strokeLinejoin="round" />
                {zoneOn[i] && <polyline className="hv-flow" points={pts} fill="none" stroke={C.cyan} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" />}
              </g>
            );
          });
        })()}

        {/* indoor cassettes + cool-air cones + data tags (click to toggle) */}
        {zones.map((z, i) => {
          const cpos = cassette(i);
          const cc = P(cpos[0], cpos[1], cpos[2]);
          const fl = zoneCenter(i);
          const on = zoneOn[i];
          const t = num(z.tempKey);
          const over = t != null && t > setpoint + 1;
          const col = on ? (over ? C.red : C.green) : C.mut;
          // air cone: from cassette down to a floor ellipse (parallelogram approx)
          const f0 = P(fl[0] - 1.1, fl[1] - 1.1, 0), f1 = P(fl[0] + 1.1, fl[1] - 1.1, 0), f2 = P(fl[0] + 1.1, fl[1] + 1.1, 0), f3 = P(fl[0] - 1.1, fl[1] + 1.1, 0);
          return (
            <g key={z.tempKey} style={{ cursor: 'pointer' }} onClick={() => toggleZone(i)}>
              {on && <polygon className="hv-air" points={poly([cc[0] - 12, cc[1] + 6], [cc[0] + 12, cc[1] + 6], f1, f2, f3, f0)} fill="url(#hv-airgrad)" />}
              {/* cassette body (small iso box) */}
              <polygon points={poly([cc[0] - 16, cc[1] - 8], [cc[0] + 16, cc[1] - 8], [cc[0] + 16, cc[1] + 6], [cc[0] - 16, cc[1] + 6])} fill={PANEL} stroke={col} strokeWidth={on ? 1.8 : 1.2} />
              <line x1={cc[0] - 8} y1={cc[1] - 1} x2={cc[0] + 8} y2={cc[1] - 1} stroke={col} strokeWidth={1.4} strokeOpacity={0.7} />
              {/* data tag */}
              <g>
                <rect x={cc[0] - 44} y={cc[1] - 58} width={88} height={40} rx={8} fill={PANEL} stroke={col} strokeWidth={1.3} />
                <text x={cc[0]} y={cc[1] - 43} textAnchor="middle" fontSize={9} fill={C.mut}>{z.name}</text>
                <text x={cc[0] - 6} y={cc[1] - 26} textAnchor="middle" fontSize={17} fontWeight={800} fill={over ? C.red : C.txt} style={tnum}>{t != null ? t.toFixed(1) : NO_VALUE}°</text>
                <text x={cc[0] + 30} y={cc[1] - 26} textAnchor="end" fontSize={8.5} fontWeight={800} fill={col}>{on ? 'ON' : 'OFF'}</text>
                <line x1={cc[0]} y1={cc[1] - 18} x2={cc[0]} y2={cc[1] - 8} stroke={col} strokeWidth={1} strokeDasharray="2 2" />
              </g>
            </g>
          );
        })}

        {/* ── rooftop: Outdoor Unit + Mspace V6 + TIS ── */}
        {(() => {
          const o = oduPos;
          const top = poly(P(o[0], o[1], o[2]), P(o[0] + 2, o[1], o[2]), P(o[0] + 2, o[1] + 1.6, o[2]), P(o[0], o[1] + 1.6, o[2]));
          const fL = poly(P(o[0], o[1] + 1.6, o[2]), P(o[0] + 2, o[1] + 1.6, o[2]), P(o[0] + 2, o[1] + 1.6, o[2] - 1.4), P(o[0], o[1] + 1.6, o[2] - 1.4));
          const fR = poly(P(o[0] + 2, o[1], o[2]), P(o[0] + 2, o[1] + 1.6, o[2]), P(o[0] + 2, o[1] + 1.6, o[2] - 1.4), P(o[0] + 2, o[1], o[2] - 1.4));
          const cTop = P(o[0] + 1, o[1] + 0.8, o[2]);
          return (
            <g>
              {oduOn && <circle cx={cTop[0]} cy={cTop[1]} r={64} fill="url(#hv-glow)" filter="url(#hv-blur)" />}
              <polygon points={fL} fill={F_L} stroke={BD} strokeWidth={1} />
              <polygon points={fR} fill={F_R} stroke={BD} strokeWidth={1} />
              <polygon points={top} fill={F_TOP} stroke={oduOn ? C.green : C.mut} strokeWidth={oduOn ? 1.8 : 1.1} />
              <g className={oduOn ? 'hv-fan' : ''}>
                {[0, 90, 180, 270].map((a) => <path key={a} d={`M ${cTop[0]} ${cTop[1]} L ${cTop[0] + 12 * Math.cos(a * Math.PI / 180)} ${cTop[1] + 6 * Math.sin(a * Math.PI / 180)}`} stroke={oduOn ? C.green : C.mut} strokeWidth={2.4} strokeLinecap="round" />)}
                <circle cx={cTop[0]} cy={cTop[1]} r={3} fill={oduOn ? C.green : C.mut} />
              </g>
              <text x={P(o[0] + 1, o[1] + 1.6, o[2] - 1.4)[0]} y={P(o[0] + 1, o[1] + 1.6, o[2] - 1.4)[1] + 14} textAnchor="middle" fontSize={9.5} fontWeight={700} fill={C.txt}>Outdoor Unit (VRF) · {fmt(num('odu_kw'), 1)}kW</text>
            </g>
          );
        })()}

        {/* control chain boxes (top-left, screen-space overlay) */}
        <g>
          <rect x={26} y={58} width={150} height={40} rx={8} fill={PANEL} stroke={online ? C.green : C.mut} strokeWidth={1.5} />
          <circle className={online ? 'hv-pulse' : ''} cx={40} cy={72} r={3.5} fill={online ? C.green : C.mut} />
          <text x={51} y={75} fontSize={11.5} fontWeight={800} fill={C.txt}>Mspace V6</text>
          <text x={40} y={90} fontSize={8.5} fill={C.mut}>{th ? 'เอดจ์เกตเวย์' : 'Edge Gateway'}</text>
          <line x1={101} y1={98} x2={101} y2={108} stroke={BD} strokeWidth={1.6} />
          <rect x={26} y={108} width={150} height={40} rx={8} fill={PANEL} stroke={C.gold} strokeWidth={1.5} />
          <text x={40} y={126} fontSize={11.5} fontWeight={800} fill={C.txt}>🎛️ TIS box</text>
          <text x={40} y={140} fontSize={8.5} fill={C.mut}>{th ? 'ควบคุม VRF/VRV' : 'VRF/VRV control'}</text>
          {/* control signal TIS → rooftop ODU */}
          <line className="hv-ctrl" x1={176} y1={128} x2={P(oduPos[0], oduPos[1] + 0.8, oduPos[2])[0] - 16} y2={P(oduPos[0], oduPos[1] + 0.8, oduPos[2])[1]} stroke={C.gold} strokeWidth={1.8} />
        </g>

        {/* legend */}
        <g fontSize={9.5} fontWeight={700}>
          <line x1={24} y1={416} x2={46} y2={416} stroke={C.cyan} strokeWidth={3} strokeLinecap="round" />
          <text x={52} y={419} fill={C.mut}>{th ? 'ท่อน้ำยา/ลมเย็น' : 'Refrigerant / cool air'}</text>
          <line x1={220} y1={416} x2={242} y2={416} stroke={C.gold} strokeWidth={2} strokeDasharray="3 5" />
          <text x={248} y={419} fill={C.mut}>{th ? 'ควบคุม (TIS)' : 'Control (TIS)'}</text>
        </g>
      </svg>
      </div>{/* /left column */}

      {/* ── combined info card (right) ── */}
      <div className="hv-side hv-card" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 11 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: '.02em', color: C.txt }}>{th ? 'สรุปค่าระบบ' : 'System Overview'}</span>
          {oat != null && <span style={{ fontSize: 11, color: C.mut }}>{WEATHER_EMOJI[weather] || '🌡️'} {th ? 'ภายนอก' : 'Outdoor'} <b style={{ color: C.txt }}>{fmt(oat, 1)}°C</b></span>}
        </div>
        {/* KPI tiles */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 8 }}>
          {kpis.map((k, i) => (
            <div key={i} className="hv-kpi" style={{ background: `linear-gradient(150deg, color-mix(in srgb, ${k.color} 16%, var(--bg-elevated)) 0%, var(--bg-elevated) 72%)`, border: `1px solid color-mix(in srgb, ${k.color} 42%, transparent)`, padding: '9px 11px' }}>
              <div style={{ fontSize: 10, color: C.mut, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{k.label}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 3 }}>
                <span style={{ fontSize: 22, fontWeight: 800, color: k.color, textShadow: `0 0 18px color-mix(in srgb, ${k.color} 45%, transparent)`, ...tnum }}>{k.value}</span>
                {k.unit && <span style={{ fontSize: 10, color: C.mut, fontWeight: 600 }}>{k.unit}</span>}
              </div>
            </div>
          ))}
        </div>

        {/* 🤖 AI Optimization */}
        <div className={`hv-sub${aiOn ? ' hv-glowon' : ''}`} style={{ padding: 12, ...(aiOn ? { borderColor: C.green } : {}) }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 11.5, fontWeight: 800, color: C.txt }}>🤖 AI Optimization</span>
            <button onClick={toggleAi} style={{ fontSize: 10.5, fontWeight: 800, padding: '4px 12px', borderRadius: 999, cursor: 'pointer', border: `1px solid ${aiOn ? C.green : BD}`, background: aiOn ? 'color-mix(in srgb, var(--green) 16%, transparent)' : 'transparent', color: aiOn ? C.green : C.mut }}>{aiOn ? 'AUTO ● ON' : 'AUTO ○ OFF'}</button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: C.mut }}>{th ? 'เป้าลดไฟวันนี้' : 'Cut power today'}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button onClick={() => bumpRed(-5)} style={miniBtn}>−</button>
              <span style={{ fontSize: 18, fontWeight: 800, color: C.green, minWidth: 46, textAlign: 'center', ...tnum }}>{redPct}%</span>
              <button onClick={() => bumpRed(5)} style={miniBtn}>+</button>
            </div>
          </div>
          <div style={{ fontSize: 11, color: C.mut, marginTop: 6 }}>
            {th ? 'ปกติ' : 'Baseline'} <b style={{ color: C.txt }}>{fmt(baseKwh, 0)}</b> → {th ? 'เป้า' : 'target'} <b style={{ color: C.green }}>{fmt(aiTargetKwh, 0)} kWh</b>
          </div>
          {aiOn ? (
            <div style={{ marginTop: 8, borderTop: `1px dashed ${BD}`, paddingTop: 8 }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: C.green, marginBottom: 6 }}>✓ {th ? 'AI กำลังปรับระบบ · คาดจบวัน' : 'AI optimizing · projected'} {fmt(effectiveProj, 0)} kWh (−{redPct}%)</div>
              <ul style={{ margin: 0, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 3 }}>
                {aiActions.map((a, i) => <li key={i} style={{ fontSize: 10.5, color: C.mut }}>{a}</li>)}
              </ul>
            </div>
          ) : (
            <div style={{ fontSize: 10.5, color: C.mut, marginTop: 8 }}>{th ? 'เปิด AUTO เพื่อให้ AI ปรับระบบให้ถึงเป้าอัตโนมัติ' : 'Turn on AUTO — AI adjusts the system to hit the target'}</div>
          )}
        </div>

        {/* instrument panels (nested — elevated bg for contrast) */}
        <Panel title={`💨 ${th ? 'AHU / ลม' : 'AHU / Air'}`} C={C} BD={BD} PANEL={'var(--bg-elevated)'} rows={[
          ['supply_air', label('supply_air'), val('supply_air')], ['return_air', label('return_air'), val('return_air')],
          ['ahu_fan', label('ahu_fan'), num('ahu_fan') ? (th ? 'ทำงาน' : 'RUN') : (th ? 'หยุด' : 'STOP')],
          ['filter', label('filter'), num('filter') ? (th ? 'ต้องเปลี่ยน' : 'ALARM') : 'OK'],
          ['filter_dp', label('filter_dp'), val('filter_dp')],
        ]} />
        {(() => {
          const used = num('daily'), budget = num('energy_budget'), proj = effectiveProj;
          if (budget == null && used == null) return null;
          const pct = budget ? Math.min(100, Math.round(((used ?? 0) / budget) * 100)) : 0;
          const projPct = budget && proj != null ? Math.min(100, (proj / budget) * 100) : null;
          const over = proj != null && budget != null && proj > budget;
          const fillCol = over ? C.red : C.green;
          return (
            <div className="hv-sub" style={{ padding: 12 }}>
              <div className="hv-title" style={{ marginBottom: 8 }}>🎯 {th ? 'พลังงานวันนี้ vs งบ' : 'Energy vs Budget'}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: C.mut }}>{th ? 'ใช้แล้ว' : 'Used'} <b style={{ color: C.txt, fontSize: 16, ...tnum }}>{fmt(used, 0)}</b> / {fmt(budget, 0)} kWh</span>
                <span style={{ fontSize: 13, fontWeight: 800, color: fillCol }}>{pct}%</span>
              </div>
              <div style={{ position: 'relative', height: 10, borderRadius: 6, background: 'color-mix(in srgb, var(--text-muted) 22%, transparent)', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${pct}%`, background: `linear-gradient(90deg, color-mix(in srgb, ${fillCol} 65%, var(--cyan)), ${fillCol})`, borderRadius: 6, boxShadow: `0 0 12px -2px ${fillCol}` }} />
                {projPct != null && <div style={{ position: 'absolute', top: -2, bottom: -2, left: `calc(${projPct}% - 1px)`, width: 2, background: C.gold }} />}
              </div>
              <div style={{ fontSize: 11, color: C.mut, marginTop: 7 }}>
                <span style={{ color: C.gold }}>▎</span> {th ? 'คาดจบวัน' : 'Projected'} <b style={{ color: over ? C.red : C.txt }}>{fmt(proj, 0)} kWh</b> · {over ? (th ? '⚠ เกินงบ' : '⚠ over budget') : (th ? '✓ ต่ำกว่างบ' : '✓ under budget')}
              </div>
            </div>
          );
        })()}
        <Panel title={`💰 ${th ? 'การประหยัด' : 'Savings'}`} C={C} BD={BD} PANEL={'var(--bg-elevated)'} accent={C.green} rows={[
          ['save_pct', label('save_pct'), val('save_pct')], ['save_kw', label('save_kw'), val('save_kw')], ['save_baht', label('save_baht'), val('save_baht')], ['save_kwh', label('save_kwh'), val('save_kwh')], ['co2_saved', label('co2_saved'), val('co2_saved')],
        ]} />
      </div>{/* /info card */}
      </div>{/* /row */}

      {/* ── control bar ── */}
      <div className="hv-card" style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: C.mut }}>Setpoint</span>
          <button onClick={() => bump(-0.5)} style={roundBtn}>−</button>
          <span style={{ fontSize: 22, fontWeight: 800, color: C.cyan, minWidth: 62, textAlign: 'center', ...tnum }}>{setpoint}°C</span>
          <button onClick={() => bump(0.5)} style={roundBtn}>+</button>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {MODES.map((m) => (
            <button key={m} onClick={() => pickMode(m)} style={{ fontSize: 11, fontWeight: 700, padding: '6px 12px', borderRadius: 8, cursor: 'pointer', border: '1px solid ' + (mode === m ? C.amber : BD), background: mode === m ? 'var(--amber-dim)' : 'transparent', color: mode === m ? C.amber : C.mut }}>{MODE_LABEL[m] || m}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => allZones(true)} style={{ ...pill, borderColor: C.green, color: C.green }}>{th ? 'เปิดทุกโซน' : 'All ON'}</button>
          <button onClick={() => allZones(false)} style={{ ...pill, borderColor: BD, color: C.mut }}>{th ? 'ปิดทุกโซน' : 'All OFF'}</button>
        </div>
      </div>

      <div style={{ fontSize: 10.5, color: C.mut, textAlign: 'right' }}>
        {th ? 'Mspace V6 → กล่อง TIS-VRF-AC ควบคุมแอร์ VRF (โหมดสาธิต — คำสั่งไม่ส่งจริง)' : 'Mspace V6 → TIS-VRF-AC box controls the VRF (demo — commands not sent to hardware)'}
      </div>
    </div>
  );

  function val(k: string) { const n = num(k); return n == null ? NO_VALUE : `${fmt(n, 1)}${unit(k) ? ' ' + unit(k) : ''}`; }
}

// Bilingual field labels — device.fieldNames from the DB are Thai-only, so these
// override them so the whole widget follows the selected language.
const FIELD_LABELS: Record<string, { th: string; en: string }> = {
  zone1_temp: { th: 'โซน 1 · สำนักงาน', en: 'Zone 1 · Office' },
  zone2_temp: { th: 'โซน 2 · ห้องประชุม', en: 'Zone 2 · Meeting' },
  zone3_temp: { th: 'โซน 3 · โถงต้อนรับ', en: 'Zone 3 · Lobby' },
  zone4_temp: { th: 'โซน 4', en: 'Zone 4' },
  supply_air: { th: 'ลมจ่าย', en: 'Supply Air' },
  return_air: { th: 'ลมกลับ', en: 'Return Air' },
  ahu_fan: { th: 'พัดลม AHU', en: 'AHU Fan' },
  filter: { th: 'สถานะฟิลเตอร์', en: 'Filter' },
  filter_dp: { th: 'ΔP ฟิลเตอร์', en: 'Filter ΔP' },
  save_pct: { th: 'ประหยัด %', en: 'Saved %' },
  save_kw: { th: 'ประหยัด kW', en: 'Saved kW' },
  save_baht: { th: 'ประหยัด/วัน', en: 'Saved / day' },
  save_kwh: { th: 'ประหยัดพลังงาน', en: 'Saved energy' },
  co2_saved: { th: 'CO₂ ที่ลดได้', en: 'CO₂ reduced' },
};

const MODE_LABEL: Record<string, string> = { eco: 'ECO', cool: 'COOL', comfort: 'COMFORT', off: 'OFF' };
const MODES = ['eco', 'cool', 'comfort', 'off'];
const WEATHER_EMOJI: Record<string, string> = { sunny: '☀️', cloudy: '☁️', rain: '🌧️', hot: '🥵' };

const roundBtn: React.CSSProperties = { width: 34, height: 34, borderRadius: 10, border: '1px solid var(--glass-border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: 18, fontWeight: 800, cursor: 'pointer', lineHeight: 1 };
const miniBtn: React.CSSProperties = { width: 26, height: 26, borderRadius: 8, border: '1px solid var(--glass-border)', background: 'var(--bg-deep)', color: 'var(--text-primary)', fontSize: 15, fontWeight: 800, cursor: 'pointer', lineHeight: 1 };
const pill: React.CSSProperties = { fontSize: 10.5, fontWeight: 700, padding: '6px 12px', borderRadius: 8, cursor: 'pointer', border: '1px solid', background: 'transparent' };

function Panel({ title, rows, C, BD, PANEL, accent }: { title: string; rows: [string, string, string][]; C: Record<string, string>; BD: string; PANEL: string; accent?: string }) {
  const shown = rows.filter((r) => r[2] !== NO_VALUE);
  if (shown.length === 0) return null;
  return (
    <div className="hv-sub" style={{ padding: 12, ...(accent ? { border: `1px solid color-mix(in srgb, ${accent} 45%, transparent)` } : {}) }}>
      <div className="hv-title" style={{ marginBottom: 8 }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {shown.map(([k, l, v]) => (
          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 11.5 }}>
            <span style={{ color: C.mut, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l}</span>
            <span style={{ fontWeight: 800, color: accent || C.txt, whiteSpace: 'nowrap' }}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
